import React, { FC, memo, useEffect } from 'react'
import { Slide } from 'pure-react-carousel'

import GridCard from '../../common/grid-card'
import { ComponentSeparator } from '../styles'
import PrevNextControls from '../../common/prev-next-controls'
import { Title, TitleContainer, Section } from './styles'
import { useTranslation } from '../../../hooks/locale'
import { useRequestUserConfigContext } from '../../../hooks/request'
import { listingClient } from '../../../clients'
import { SliderContainer, StyledCarouselProvider, StyledSlider } from '../../common/listing-card-section/styles'
import PropertyGridSkeleton from '../../home/continue-search-widget/skeleton'
import { ListingSectionObject } from '../../../transports/common/listing-section-transport'
import { useResponsiveValue } from '../../../hooks/responsive'
import { useAsyncCallback } from '../../../hooks/async'
import { cardEventCategories, pageTypeEventCategories } from '../../../services/analytics-service/categories'
import AnalyticsService from '../../../services/analytics-service'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'
import { Offer } from '../../../models/common/offers-model'
import { SuitablesModel } from '../../../models/common/suitables-model'

interface SimilarPropertyProps {
    listingId: number
    regionId: number
    filter: string
    arrival?: string
    departure?: string
    persons?: number
    sitePathname?: string
    url?: string
    breakpoints?: { query: string; value: number }[]
    pageType: string
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
}

const DEFAULT_SIMILAR_PROPERTY_BREAKPOINTS = [
    { query: '(min-width: 768px)', value: 2 },
    { query: '(max-width: 767px)', value: 1.5 },
]

const SimilarProperty: FC<SimilarPropertyProps> = ({
    listingId,
    regionId,
    filter,
    arrival,
    departure,
    persons,
    sitePathname,
    url,
    breakpoints = DEFAULT_SIMILAR_PROPERTY_BREAKPOINTS,
    pageType,
    breadcrumbModel,
    offerModel,
    suitablesModel,
}) => {
    const { t } = useTranslation()
    const user = useRequestUserConfigContext()

    const visibleSlides = useResponsiveValue(breakpoints, 1.5)
    const dragStep = Math.floor(visibleSlides)

    useEffect(() => {
        AnalyticsService.trackViewEvent(pageType, cardEventCategories.sections.SIMILAR_PROPERTIES)
    }, [pageType])

    const loadSimilar = useAsyncCallback(async () => {
        const params = {
            filter,
            listingId,
            regionId,
            limit: 12,
            ...(arrival && { arrival }),
            ...(departure && { departure }),
            ...(typeof persons === 'number' && persons > 0 && { persons }),
        }

        const res = await listingClient.getPdpRecommendedObjects({
            user,
            params,
        })

        return res?.data?.objects ?? []
    }, [])

    useEffect(() => {
        if (listingId && regionId) {
            loadSimilar.callback()
        }
    }, [listingId, regionId, filter, arrival, departure, persons])

    const isLoading = loadSimilar.isLoading
    const property = loadSimilar.data as ListingSectionObject[] | null

    if (!isLoading && (!property || property.length === 0)) {
        return null
    }

    return (
        <Section id="similar-properties">
            <TitleContainer>
                <Title>{t('accomodations.similarPropertySectionTitle')}</Title>
            </TitleContainer>

            {isLoading && (
                <>
                    <PropertyGridSkeleton count={2} />
                    <ComponentSeparator />
                </>
            )}

            {!isLoading && property && property.length > 0 && (
                <>
                    <SliderContainer>
                        <StyledCarouselProvider
                            totalSlides={property.length}
                            visibleSlides={visibleSlides}
                            naturalSlideWidth={1}
                            naturalSlideHeight={1.3}
                            isIntrinsicHeight
                            lockOnWindowScroll
                            dragStep={dragStep}
                            step={dragStep}
                        >
                            <PrevNextControls totalObjects={property.length} visibleSlides={visibleSlides} />

                            <StyledSlider $transitionTime={0.5}>
                                {property.map((item, index) => (
                                    <Slide index={index} key={item.id}>
                                        <GridCard
                                            listingObject={item}
                                            requestUrl={url}
                                            sitePathname={sitePathname}
                                            pageName={pageTypeEventCategories.PROPERTY_DETAILS_PAGE}
                                            sectionName={cardEventCategories.sections.SIMILAR_PROPERTIES}
                                            breadcrumbModel={breadcrumbModel}
                                            offerModel={offerModel}
                                            suitablesModel={suitablesModel}
                                            showFeatures={false}
                                        />
                                    </Slide>
                                ))}
                            </StyledSlider>
                        </StyledCarouselProvider>
                    </SliderContainer>

                    <ComponentSeparator />
                </>
            )}
        </Section>
    )
}

export default memo(SimilarProperty)
